import { httpClient } from '../../../api/httpClient';
import { ConfigFileStatus, ConfigFileStatusType } from '../../../entities/ConfigFileStatus';
import { InventorizationIncorrectStatus, InventorizationStatus } from '../../../entities/InventorizationStatus';
import { downloadFile } from '../../../utils/download';
import { ConfigFile } from '../contexts/TerminalContext';

export interface ConfigFileFilter {
  status?: ConfigFileStatus;
  no_range?: boolean;
}

interface UploadedConfigFileResponseBody {
  original: {
    file_name: string;
    file_ext: string;
    file_path: string;
    filename: string;
    src: string;
    file_mime_type: string;
  };
  file: {
    file_name: string;
    file_ext: string;
    file_path: string;
    filename: string;
    src: string;
    file_mime_type: string;
  };
}

export interface UploadedConfigFileResponse {
  body: UploadedConfigFileResponseBody;
}

export interface AssignStoragePlacesPayload {
  company_id?: number;
  product_article_id?: number;
  place_addresses: number[];
}

export interface AssignStoragePlacesResponse {
  success: boolean;
}

export interface PalletInfo {
  pallet_id: number;
  pallet_sscc: string | null;
  ceel_number: string | null;
}

export interface InventorizationStatusData {
  id: number;
  place_id?: number | null;
  status: InventorizationStatus | null;
  incorrect_status?: InventorizationIncorrectStatus | null;
  note?: string | null;
  sscc?: string | null;
  place_address?: string | null;
  /** Set once the scan was matched to a pallet the system knows. */
  pallet_id?: number | null;
  /** The pallet number the scan was matched by, or the one the operator typed. */
  pallet_ext_id?: string | null;
  /** The incoming order the pallet arrived with: taken from the match, or picked by the operator. */
  order_id?: number | null;
  order_ref?: string | null;
  /** Boxes counted on the scanned pallet: copied from the matched pallet, corrected by hand. */
  quantity?: number | null;
  pallets?: PalletInfo[];
}

/** The pallet fields an initial inventorization matches a scan against. */
export interface InitialInventorizationPallet {
  id: number;
  ext_id: string | null;
  sscc: string | null;
  /** The incoming order the pallet arrived with, filled into the order field on a match. */
  order_id: number | null;
  order_ref: string | null;
  /** The boxes the system currently holds for the pallet, proposed as the counted quantity on a match. */
  quantity: number | null;
}

/** An incoming order an operator can attribute a found pallet to by hand. */
export interface IncomingInventorizationOrder {
  order_id: number;
  reference: string | null;
  eta_date: string | null;
  company_name: string | null;
  commodity_name: string | null;
}

export interface RemainingPalletRow {
  sscc: string | null;
  order_id: number;
  order_ref: string | null;
  company_id: number | null;
  company_name: string | null;
  product_name: string | null;
  quantity: number | string | null;
  net_weight: number | string | null;
  ext_id: number | string | null;
}

export interface RemainingPalletsPreview {
  rows: RemainingPalletRow[];
  invalid_rows: RemainingPalletRow[];
}

export interface GenerateRemainingPalletsResult {
  created: number;
}

export const configApi = {
  async fetchConfigFiles(terminalId: number, filter?: ConfigFileFilter, signal?: AbortSignal): Promise<ConfigFile[]> {
    const defaultFilter: ConfigFileFilter = { no_range: true };
    const actualFilter = filter ? { ...defaultFilter, ...filter } : defaultFilter;

    const { json: data } = await httpClient(
      `/storage/terminal/${terminalId}/configs?filter=${encodeURIComponent(JSON.stringify(actualFilter))}`,
      { signal }
    );
    return data || [];
  },

  async fetchReadyConfigFileContent(terminalId: number, src: string, signal?: AbortSignal): Promise<any> {
    const { json: configData } = await httpClient(
      `/storage/terminal/${terminalId}/configs/${src}`,
      { signal }
    );
    return configData;
  },

  async fetchStoragePlaces(terminalId: number, signal?: AbortSignal): Promise<any> {
    const { json: data } = await httpClient(
      `/storage-places?filter=${encodeURIComponent(JSON.stringify({ no_range: true, terminal_id: terminalId }))}`,
      { signal }
    );
    return data;
  },

  async fetchBuildingPorts(terminalId: number, signal?: AbortSignal): Promise<any> {
    const { json: data } = await httpClient(
      `/ports?filter=${encodeURIComponent(JSON.stringify({ no_range: true, terminal_id: [terminalId] }))}`,
      { signal }
    );
    return data;
  },

  async postConfigFile(
    terminalId: number,
    file: File,
    status: ConfigFileStatusType = ConfigFileStatus.draft
  ): Promise<UploadedConfigFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('status', String(status));

    const { json } = await httpClient(
      `/files/storage/terminal/${terminalId}/configs`,
      {
        method: 'POST',
        body: formData,
      }
    );
    return json as UploadedConfigFileResponse;
  },

  async saveReadyConfig(terminalId: number, src: string, readyConfig: any, status: ConfigFileStatusType): Promise<void> {
    await httpClient(
      `/storage/terminal/${terminalId}/configs/${src}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          status: status,
          areaBoundaries: readyConfig.areaBoundaries,
          rowBoundaries: readyConfig.rowBoundaries,
          portBoundaries: readyConfig.portBoundaries,
        }),
      }
    );
  },

  async assignStoragePlaces(payload: AssignStoragePlacesPayload): Promise<AssignStoragePlacesResponse> {
    const { json: data } = await httpClient('/storage-place-assignments', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },

  async fetchStorageEntities(storageEntityIds: number[], signal?: AbortSignal): Promise<any[]> {
    if (storageEntityIds.length === 0) {
      return Promise.resolve([]);
    }

    const filter = { storage_entity_ids: storageEntityIds };
    const encodedFilter = encodeURIComponent(JSON.stringify(filter));
    const url = `/storage-entities?filter=${encodedFilter}`;

    try {
      const { json: data } = await httpClient(url, { signal });
      return data || [];
    } catch (error) {
      // Retry once if request failed (status 0 or network error)
      console.warn('First storage-entities request failed, retrying...', error);
      try {
        const { json: data } = await httpClient(url, { signal });
        return data || [];
      } catch (retryError) {
        console.error('Second storage-entities request also failed', retryError);
        return [];
      }
    }
  },

  /** A place holds one record per scanned pallet; they are returned in scan order. */
  async getInventorizationStatuses(terminalId: number, placeId: number): Promise<InventorizationStatusData[]> {
    const filterParam = encodeURIComponent(JSON.stringify({
      terminal_id: terminalId,
      place_id: placeId,
      no_range: true,
    }));
    const sortParam = encodeURIComponent(JSON.stringify(['id', 'ASC']));
    const { json } = await httpClient(
      `/storage-inventorization-statuses?filter=${filterParam}&sort=${sortParam}`
    );
    return json || [];
  },

  async updateInventorizationStatus(
    terminalId: number,
    statusId: number,
    quantity: number | null,
    note: string | null,
    orderId: number | null
  ): Promise<InventorizationStatusData> {
    const { json } = await httpClient(`/storage-inventorization-statuses/${statusId}`, {
      method: 'PATCH',
      body: JSON.stringify({ terminal_id: terminalId, quantity, note, order_id: orderId }),
    });
    return json;
  },

  async deleteInventorizationStatus(terminalId: number, statusId: number): Promise<void> {
    await httpClient(
      `/storage-inventorization-statuses/${statusId}?terminal_id=${terminalId}`,
      { method: 'DELETE' }
    );
  },

  async clearInventorizationStatuses(terminalId: number, placeId: number): Promise<void> {
    await httpClient(
      `/storage-inventorization-statuses?terminal_id=${terminalId}&place_id=${placeId}`,
      { method: 'DELETE' }
    );
  },

  /** Every pallet of the terminal in one response — initial inventorization matches against all of it. */
  async getInitialInventorizationPallets(
    terminalId: number,
    signal?: AbortSignal
  ): Promise<InitialInventorizationPallet[]> {
    const { json } = await httpClient(
      `/storage-initial-inventorization/pallets-data?terminal_id=${terminalId}`,
      { signal }
    );
    return json || [];
  },

  /** The recent incoming orders of the terminal, for attributing an unmatched pallet by hand. */
  async getIncomingInventorizationOrders(
    terminalId: number,
    signal?: AbortSignal
  ): Promise<IncomingInventorizationOrder[]> {
    const { json } = await httpClient(
      `/storage-initial-inventorization/incoming-orders?terminal_id=${terminalId}`,
      { signal }
    );
    return json || [];
  },

  async getRemainingPallets(terminalId: number): Promise<RemainingPalletsPreview> {
    const { json } = await httpClient(`/storage-inventorization/remaining-pallets?terminal_id=${terminalId}`);
    return json;
  },

  async generateRemainingPallets(terminalId: number): Promise<GenerateRemainingPalletsResult> {
    const { json } = await httpClient('/storage-inventorization/remaining-pallets/generate', {
      method: 'POST',
      body: JSON.stringify({ terminal_id: terminalId }),
    });
    return json;
  },

  async searchStoragePlaces(
    terminalId: number,
    searchValue: string,
    searchFields: Array<'address' | 'ceel_number' | 'sscc' | 'old_address'> = ['address']
  ): Promise<number[]> {
    const filter = {
      terminal_id: terminalId,
      no_range: true,
      search_fields: searchFields,
      q: searchValue,
    };
    const encodedFilter = encodeURIComponent(JSON.stringify(filter));
    const { json: data } = await httpClient(
      `/storage-places-lookup?filter=${encodedFilter}`,
      { method: 'GET' }
    );
    return data ? data.map((place: { id: number }) => place.id) : [];
  },

  async downloadSinglePlaceQrCode(placeId: number, placeAddress: string): Promise<void> {
    const url = `/storage-places/${placeId}/get-address-qr-code`;
    const filename = `storage_place_${placeAddress}_qr_code.pdf`;
    await downloadFile(url, filename);
  },

  async downloadColumnQrCodes(placeId: number): Promise<void> {
    const url = `/storage-places/get-address-qr-code?place_id=${placeId}`;
    const filename = `storage_places_column_qr_codes.pdf`;
    await downloadFile(url, filename);
  },

  async downloadAllTerminalQrCodes(terminalId: number): Promise<void> {
    const url = `/storage-places/get-all-address-qr-codes?terminal_id=${terminalId}`;
    const filename = `terminal_${terminalId}_all_qr_codes.pdf`;
    await downloadFile(url, filename);
  },
};
